export const RFQ_STATUS_OPTIONS = [
  'All Status',
  'Draft',
  'Sent',
  'Quoted',
  'Ordered',
  'Delivered',
  'Closed',
] as const;

export type RfqStatusFilter = (typeof RFQ_STATUS_OPTIONS)[number];

export const RFQ_FILTER_BUCKETS: Exclude<RfqStatusFilter, 'All Status'>[] = [
  'Draft',
  'Sent',
  'Quoted',
  'Ordered',
  'Delivered',
  'Closed',
];

/** Canonical lowercase values sent to the API (primary token per bucket). */
export const RFQ_STATUS_API_MAP: Record<Exclude<RfqStatusFilter, 'All Status'>, string> = {
  Draft: 'draft',
  Sent: 'sent',
  Quoted: 'quoted',
  Ordered: 'ordered',
  Delivered: 'delivered',
  Closed: 'closed',
};

/**
 * All raw DB/API status strings that belong to each filter bucket.
 * Keep in sync with apps/api/src/services/rfq.service.ts RFQ_STATUS_FILTER_ALIASES.
 */
export const RFQ_STATUS_DB_ALIASES: Record<Exclude<RfqStatusFilter, 'All Status'>, string[]> = {
  Draft: ['draft'],
  Sent: ['sent', 'pending'],
  Quoted: ['quoted', 'quote-sent', 'quotes-received'],
  Ordered: ['ordered', 'finalized', 'awarded', 'partially_ordered', 'order-confirmed'],
  Delivered: ['delivered'],
  Closed: ['closed', 'completed', 'cancelled', 'rejected_by_all'],
};

export function normalizeRfqStatus(status?: string | null): string {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

const FILTER_LABEL_BY_RAW: Record<string, RfqStatusFilter> = {};

for (const bucket of RFQ_FILTER_BUCKETS) {
  for (const raw of RFQ_STATUS_DB_ALIASES[bucket]) {
    FILTER_LABEL_BY_RAW[normalizeRfqStatus(raw)] = bucket;
  }
}

/** Map any raw backend status to a filter bucket label, or null if unknown. */
export function resolveStatusFilterBucket(status?: string | null): RfqStatusFilter | null {
  const normalized = normalizeRfqStatus(status);
  if (!normalized) return null;
  return FILTER_LABEL_BY_RAW[normalized] ?? null;
}

export function toDisplayStatus(status?: string | null): string {
  const bucket = resolveStatusFilterBucket(status);
  if (bucket) return bucket;
  const normalized = normalizeRfqStatus(status);
  if (!normalized) return 'Unknown';
  return normalized
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getApiStatusParam(filter: RfqStatusFilter): string | undefined {
  if (filter === 'All Status') return undefined;
  return RFQ_STATUS_API_MAP[filter];
}

export function getStatusDbVariants(filter: RfqStatusFilter): string[] {
  if (filter === 'All Status') return [];
  return RFQ_STATUS_DB_ALIASES[filter] ?? [RFQ_STATUS_API_MAP[filter]];
}

export function rfqMatchesStatusFilter(
  rfqStatus: string | undefined | null,
  filter: RfqStatusFilter
): boolean {
  if (filter === 'All Status') return true;
  const bucket = resolveStatusFilterBucket(rfqStatus);
  return bucket === filter;
}

export function computeRawStatusCounts(
  rfqs: Array<{ status?: string | null }>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const rfq of rfqs) {
    const raw = String(rfq.status ?? 'unknown');
    counts[raw] = (counts[raw] ?? 0) + 1;
  }
  return counts;
}

export function computeFilterBucketCounts(
  rfqs: Array<{ status?: string | null }>
): Partial<Record<RfqStatusFilter, number>> {
  const counts: Partial<Record<RfqStatusFilter, number>> = {
    'All Status': rfqs.length,
  };

  for (const bucket of RFQ_FILTER_BUCKETS) {
    counts[bucket] = 0;
  }

  for (const rfq of rfqs) {
    const bucket = resolveStatusFilterBucket(rfq.status);
    if (bucket) {
      counts[bucket] = (counts[bucket] ?? 0) + 1;
    }
  }

  return counts;
}

export function collectUniqueDisplayStatuses(statuses: Array<string | undefined | null>): string[] {
  const labels = new Set<string>();
  for (const status of statuses) {
    labels.add(toDisplayStatus(status));
  }
  return RFQ_FILTER_BUCKETS.filter((option) => labels.has(option));
}

export function getRfqStatusBadgeClasses(status?: string | null): string {
  const bucket = resolveStatusFilterBucket(status);
  switch (bucket) {
    case 'Draft':
      return 'border-amber-500/30 bg-amber-500/15 text-amber-400 shadow-sm';
    case 'Sent':
      return 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400 shadow-sm';
    case 'Quoted':
      return 'border-purple-500/30 bg-purple-500/15 text-purple-400 shadow-sm';
    case 'Ordered':
      return 'border-[#0088cc]/30 bg-[#0088cc]/15 text-[#38bdf8] shadow-sm';
    case 'Delivered':
      return 'border-teal-500/30 bg-teal-500/15 text-teal-400 shadow-sm';
    case 'Closed':
      return 'border-white/10 bg-white/5 text-white/50 shadow-sm';
    default:
      return 'border-white/10 bg-white/5 text-white/40';
  }
}

export function logRfqStatusFilterDebug(payload: {
  rfqs: Array<{ status?: string | null }>;
  selectedStatus: RfqStatusFilter;
  filteredStatuses: string[];
  filteredResultCount: number;
  apiUrl?: string;
  queryParams?: Record<string, string>;
}): void {
  const rawCounts = computeRawStatusCounts(payload.rfqs);
  const bucketCounts = computeFilterBucketCounts(payload.rfqs);
  const uniqueRaw = Object.keys(rawCounts).sort();
  const allStatuses = payload.rfqs.map((rfq) => String(rfq.status ?? 'unknown'));

  console.group('[RFQ Status Filter]');
  console.log('selectedStatus:', payload.selectedStatus);
  console.log('All RFQ statuses:', allStatuses);
  console.log('Filtered result statuses:', payload.filteredStatuses);
  console.log('Unique raw API statuses:', uniqueRaw);
  console.log('Raw status counts:', rawCounts);
  console.log('Status counts:');
  for (const bucket of RFQ_FILTER_BUCKETS) {
    console.log(`  ${bucket}: ${bucketCounts[bucket] ?? 0}`);
  }
  console.log('Filtered result count:', payload.filteredResultCount);
  if (payload.apiUrl) {
    console.log('API URL:', payload.apiUrl);
  }
  if (payload.queryParams) {
    console.log('Query params:', payload.queryParams);
  }

  console.table(
    uniqueRaw.map((raw) => ({
      rawBackendStatus: raw,
      normalized: normalizeRfqStatus(raw),
      displayBadge: toDisplayStatus(raw),
      filterBucket: resolveStatusFilterBucket(raw) ?? '(unmapped)',
    }))
  );
  console.groupEnd();
}
